import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Button, Grid, Link, Typography, useMediaQuery, useTheme } from '@mui/material';
import { AddCircleOutlineOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import FHButton from '../../../../../../components/base/FHButton';
import FHDurationTag from '../../../../../../components/base/FHDurationTag';
import FHDurationTrialTime from '../../../../../../components/base/fhDurationTrialTime';
import FHStopwatch from '../../../../../../components/base/FHStopwatch';
import { FOutlinedValue } from '../../../../../../components/baseV2';
import { sessionActions } from '../../../../../../store';
import { FORTA_BLUE, FORTA_ERROR, FORTA_WHITE } from '../../../../../../utils/colors';
import TimeTagStartIconComp from '../../../../../../components/svgs/TimeTagStartIcon';
import TimeTagEndIconComp from '../../../../../../components/svgs/TimeTagEndIcon';
import { DurationEventField, DurationStopwatch } from './components';
import { COLLAPSED_EVENT_COUNT } from './constants';
import {
  DURATION_TRACKER_V2_ACTIONS_SX,
  DURATION_TRACKER_V2_ADD_BUTTON_ROW_SX,
  DURATION_TRACKER_V2_ADD_BUTTON_SX,
  DURATION_TRACKER_V2_BUTTON_SX,
  DURATION_TRACKER_V2_CONTAINER_SX,
  DURATION_TRACKER_V2_EMPTY_SX,
  DURATION_TRACKER_V2_EVENTS_GRID_SX,
  DURATION_TRACKER_V2_LINK_SX,
  DURATION_TRACKER_V2_TIMER_FIELD_SX,
  DURATION_TRACKER_V2_TITLE_SX,
  DURATION_TRACKER_V2_TOTAL_FIELD_SX,
  DURATION_TRACKER_V2_TOP_ROW_SX,
  DURATION_TRACKER_V2_WARNING_ROW_SX,
} from './styles';
import { formatDurationTime } from './utils';

import type { RootState } from '../../../../../../store';
import type { ITargetTrialDurationEvent } from '../../../../../../utils/types';
import type { DurationTimeTrackerProps } from './types';

function DurationTimeTracker({
  trailId,
  sessionId,
  isEditable,
  durationEvents,
  useNewUi = false,
}: DurationTimeTrackerProps) {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const [runTimer, setRunTimer] = useState<boolean>(false);
  const [collapseEvents, setCollapseEvents] = useState<boolean>(false);
  const [showAllV2Events, setShowAllV2Events] = useState<boolean>(false);
  const { t } = useTranslation('sessionNotes');
  const { current: session } = useSelector((state: RootState) => state.sessions);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const sessionDuration = session?.expected_duration ?? 0;

  const totalTrialDuration = useMemo(
    () => durationEvents?.reduce((acc, duration) => acc + (duration?.duration ?? 0), 0),
    [durationEvents],
  );

  const availableTimeForDurations = sessionDuration - totalTrialDuration;

  const handleAddNewDurationEvent = useCallback(async (duration: number) => {
    if (duration) {
      await dispatch(
        sessionActions.createDurationEvent({
          getAccessTokenSilently,
          sessionId: sessionId,
          trailId,
          data: {
            duration,
          },
        }),
      ).unwrap();
      setCollapseEvents(false);
      setShowAllV2Events(true);
    }
  }, [dispatch, getAccessTokenSilently, sessionId, trailId]);

  const handleUpdateDurationEvent = useCallback(async (durationEvent?: ITargetTrialDurationEvent) => {
    try {
      if (durationEvent?.id) {
        await dispatch(
          sessionActions.updateDurationEvent({
            getAccessTokenSilently,
            sessionId: sessionId,
            trailId,
            durationEventId: durationEvent.id,
            data: {
              duration: durationEvent.duration,
            },
          }),
        ).unwrap();
      }
      return true;
    } catch {
      return false;
    }
  }, [dispatch, getAccessTokenSilently, sessionId, trailId]);

  const handleDeleteDurationEvent = useCallback((durationEventId: number) => {
    dispatch(
      sessionActions.deleteDurationEvent({
        getAccessTokenSilently,
        sessionId: sessionId,
        trailId,
        durationEventId,
      }),
    );
  }, [dispatch, getAccessTokenSilently, sessionId, trailId]);

  const handleStartTimer = () => {
    setRunTimer(true);
    setCollapseEvents(false);
    setShowAllV2Events(true);
  };

  const handleStopTimer = () => {
    setRunTimer(false);
    setCollapseEvents(false);
  };

  const handleStopwatchStop = useCallback((duration: number) => {
    void handleAddNewDurationEvent(duration);
    setRunTimer(false);
  }, [handleAddNewDurationEvent]);

  const handleAddEventButtonClick = () => {
    void handleAddNewDurationEvent(1);
  };

  const handleToggleV2DurationEvents = () => {
    setShowAllV2Events((prev) => !prev);
  };

  const durationEventsToDisplay = useNewUi
    ? isMobile && !showAllV2Events && durationEvents.length > COLLAPSED_EVENT_COUNT
      ? durationEvents?.slice(0, COLLAPSED_EVENT_COUNT)
      : durationEvents
    : collapseEvents
      ? durationEvents?.slice(0, COLLAPSED_EVENT_COUNT)
      : durationEvents;

  if (useNewUi) {
    return (
      <Box data-testid="session-duration-tracker-container" sx={DURATION_TRACKER_V2_CONTAINER_SX}>
        <Box sx={DURATION_TRACKER_V2_TOP_ROW_SX}>
          <Typography sx={DURATION_TRACKER_V2_TITLE_SX}>
            {t('goalsPracticed.durationTracker')}
          </Typography>
          <FOutlinedValue
            label={t('goalsPracticed.totalDuration')}
            value={formatDurationTime(totalTrialDuration)}
            sx={DURATION_TRACKER_V2_TOTAL_FIELD_SX}
          />
          {isEditable && (
            <>
              <Box sx={DURATION_TRACKER_V2_ACTIONS_SX}>
                <Button
                  sx={DURATION_TRACKER_V2_BUTTON_SX}
                  disabled={runTimer || !isEditable || availableTimeForDurations < 1}
                  onClick={handleStartTimer}
                >
                  {t('goalsPracticed.start')}
                </Button>
                <Button
                  sx={DURATION_TRACKER_V2_BUTTON_SX}
                  disabled={!runTimer || !isEditable || availableTimeForDurations < 1}
                  onClick={handleStopTimer}
                >
                  {t('goalsPracticed.stop')}
                </Button>
              </Box>
              <Box sx={DURATION_TRACKER_V2_TIMER_FIELD_SX}>
                <DurationStopwatch
                  startTimer={runTimer}
                  timerLimit={availableTimeForDurations}
                  onStop={handleStopwatchStop}
                />
              </Box>
            </>
          )}
        </Box>
        {runTimer && (
          <Box sx={DURATION_TRACKER_V2_WARNING_ROW_SX}>
            <Typography fontSize={12} color={FORTA_ERROR}>
              Please stay on this page while the timer runs to avoid data loss.
            </Typography>
          </Box>
        )}
        {durationEvents.length > 0 ? (
          <Box sx={DURATION_TRACKER_V2_EVENTS_GRID_SX}>
            {durationEventsToDisplay?.map((durationEvent, index) => (
              <DurationEventField
                key={durationEvent.id ?? `duration-${index}`}
                label={`Duration ${index + 1}`}
                duration={durationEvent.duration ?? 1}
                durationLimitInSeconds={availableTimeForDurations}
                editable={isEditable}
                disabled={!isEditable || runTimer}
                required
                onDelete={() => {
                  handleDeleteDurationEvent(durationEvent.id ?? 0);
                }}
                onUpdate={(duration) => {
                  void handleUpdateDurationEvent({ ...durationEvent, duration });
                }}
              />
            ))}
          </Box>
        ) : (
          <Typography sx={DURATION_TRACKER_V2_EMPTY_SX}>
            {t('goalsPracticed.noEventsAdded')}
          </Typography>
        )}
        {isMobile && durationEvents.length > COLLAPSED_EVENT_COUNT && (
          <Link onClick={handleToggleV2DurationEvents} sx={DURATION_TRACKER_V2_LINK_SX}>
            {showAllV2Events ? t('goalsPracticed.seeLess') : t('goalsPracticed.seeMore')}
          </Link>
        )}
        {isEditable && (
          <Box sx={DURATION_TRACKER_V2_ADD_BUTTON_ROW_SX}>
            <Button
              sx={DURATION_TRACKER_V2_ADD_BUTTON_SX}
              disabled={!isEditable || runTimer || availableTimeForDurations < 1}
              onClick={handleAddEventButtonClick}
            >
              {t('goalsPracticed.addEvent')}
            </Button>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box
      data-testid="session-duration-tracker-container"
      sx={{
        mt: 1,
        ml: { sm: 2, xs: 0.2 },
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '8px',
        backgroundColor: FORTA_WHITE,
        border: `1px solid ${FORTA_BLUE.light2}`,
        padding: '8px 16px',
        width: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          height: '100%',
          pb: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            alignItems: 'flex-start',
            pt: 1,
            pb: { xs: 2, sm: 0 },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'top',
              width: '100%',
            }}
          >
            <Box sx={{ display: 'flex' }}>
              <Typography
                sx={{
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  textAlign: 'left',
                  mr: 1,
                }}
              >
                {`${t('goalsPracticed.durationTracker')}: `}
              </Typography>
              <Typography
                sx={{
                  borderRadius: '8px',
                  fontSize: '16px',
                  textAlign: 'left',
                  mr: 1,
                }}
              >
                {`${t('goalsPracticed.totalDuration')}: `}
              </Typography>
              <FHDurationTrialTime seconds={totalTrialDuration} />
            </Box>
            <Box sx={{ display: 'flex' }}>
              {isEditable && (
                <>
                  <FHButton
                    sx={{ mr: 1 }}
                    startIcon={
                      <Box sx={{ mr: 2 }}>
                        <TimeTagStartIconComp />
                      </Box>
                    }
                    disabled={runTimer || !isEditable || availableTimeForDurations < 1}
                    onClick={handleStartTimer}
                  >
                    {t('goalsPracticed.start')}
                  </FHButton>
                  <FHButton
                    sx={{ mr: 1 }}
                    startIcon={
                      <Box sx={{ mr: 2 }}>
                        <TimeTagEndIconComp />
                      </Box>
                    }
                    disabled={!runTimer || !isEditable || availableTimeForDurations < 1}
                    onClick={handleStopTimer}
                  >
                    {t('goalsPracticed.stop')}
                  </FHButton>
                  <FHStopwatch
                    startTimer={runTimer}
                    timerLimit={availableTimeForDurations}
                    onStop={(duration) => {
                      handleAddNewDurationEvent(duration);
                      setRunTimer(false);
                    }}
                  />{' '}
                </>
              )}
            </Box>
          </Box>
          {runTimer && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
              <Typography fontSize={12} color={FORTA_ERROR}>
                Please stay on this page while the timer runs to avoid data loss.
              </Typography>
            </Box>
          )}
          {durationEvents.length > COLLAPSED_EVENT_COUNT && (
            <Link
              onClick={() => {
                setCollapseEvents((prev) => !prev);
              }}
              sx={{
                fontSize: 14,
                fontWeight: 700,
                cursor: 'pointer',
                color: FORTA_BLUE.bright,
                '&:hover': {
                  color: FORTA_BLUE.primary,
                },
                marginBottom: '16px',
              }}
            >
              {collapseEvents ? 'see more' : 'see less'}
            </Link>
          )}
          <Grid
            xs={12}
            sm={12}
            sx={{
              display: 'flex',
              gap: 2,
              flexWrap: 'wrap',
              alignItems: 'center',
              py: 2,
            }}
          >
            {/* Dynamic duration tags */}
            {durationEvents.length > 0 ? (
              durationEventsToDisplay?.map((durationEvent) => (
                <Box
                  key={durationEvent.id || null}
                  sx={{
                    width: 222,
                    minHeight: 80,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <FHDurationTag
                    id={durationEvent.id ?? 0}
                    duration={durationEvent.duration ?? 1}
                    durationLimitInSeconds={availableTimeForDurations}
                    disabled={!isEditable || runTimer}
                    onDelete={handleDeleteDurationEvent}
                    onUpdate={(duration: number) =>
                      handleUpdateDurationEvent({ ...durationEvent, duration })
                    }
                  />
                </Box>
              ))
            ) : (
              <Typography textAlign="left" mb={1}>
                {t('goalsPracticed.noEventsAdded')}
              </Typography>
            )}
            {collapseEvents && (
              <Box
                sx={{
                  backgroundColor: FORTA_BLUE.light3,
                  width: 'auto',
                  borderRadius: '4px',
                  textAlign: 'center',
                  py: 1,
                  px: 2,
                  '&:hover': {
                    cursor: 'pointer',
                    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.25)',
                  },
                  cursor: 'pointer',
                }}
                onClick={() => {
                  setCollapseEvents(false);
                }}
              >
                <Typography fontWeight={700} sx={{ letterSpacing: '2px' }}>
                  ...
                </Typography>
              </Box>
            )}
          </Grid>
          {isEditable && (
            <Grid
              xs={12}
              sm={12}
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: 2,
                width: '100%',
              }}
            >
              <FHButton
                startIcon={<AddCircleOutlineOutlined />}
                disabled={!isEditable || runTimer || availableTimeForDurations < 1}
                onClick={handleAddEventButtonClick}
              >
                {t('goalsPracticed.addEvent')}
              </FHButton>
            </Grid>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default DurationTimeTracker;
